import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ShieldCheck, UserCheck, Search, Car, Sparkles, ClipboardCheck,
  CheckCircle2, ArrowRight, Phone, ChevronRight, MapPin,
  Navigation, Building2, Truck, HelpCircle, Video,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '../components/academy/Seo';
import BrandHero from '../components/BrandHero';
import { SERVICES, getService } from '../data/services';
import { LOCATIONS } from '../data/locations';

const ICONS = { ShieldCheck, UserCheck, Search, Car, Sparkles, ClipboardCheck, Navigation, Building2, Truck, Video };

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getService(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
        <div>
          <h1 className="text-2xl font-bold text-white mb-3">Service not found</h1>
          <Link to="/services" className="text-orange-400 hover:text-orange-300">View all CarLens services</Link>
        </div>
      </div>
    );
  }

  const Icon = ICONS[service.icon] || ShieldCheck;
  const path = `/services/${service.slug}`;
  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: service.name, path },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.name,
    description: service.metaDescription,
    provider: { '@type': 'AutomotiveBusiness', name: 'CarLens Inspections', telephone: '+13213003256', email: 'carlensauto@gmail.com', slogan: 'Know the Truth Before You Buy.' },
    areaServed: { '@type': 'AdministrativeArea', name: 'Northeast Florida' },
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo siteName="CarLens" title={service.seoTitle} description={service.metaDescription} path={path} breadcrumbs={breadcrumbs} faq={service.faqs || []} extraSchemas={[serviceSchema]} />

      <BrandHero />

      <div className="max-w-4xl mx-auto px-5 py-10 md:py-14">
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6">
          <Link to="/" className="hover:text-zinc-300">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/services" className="hover:text-zinc-300">Services</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-300">{service.name}</span>
        </nav>

        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/30 mb-5">
          <Icon className="w-7 h-7 text-orange-400" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.08]" style={{ fontFamily: 'Space Grotesk' }} data-testid="service-h1">
          {service.name}
        </h1>
        <p className="mt-4 text-lg text-orange-300 font-medium">{service.tagline}</p>
        <p className="mt-6 text-[15px] leading-8 text-zinc-300">{service.intro}</p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button onClick={() => navigate(service.cta)} className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-5 h-auto text-base font-semibold" data-testid="service-cta-btn">
            {service.ctaLabel} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <a href="tel:+13213003256" className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-600 text-white hover:bg-white/5 px-8 py-3 font-semibold transition-colors">
            <Phone className="w-4 h-4" /> 321.300.3256
          </a>
        </div>

        {/* What's included */}
        <section className="mt-14">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>What’s Included</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {service.checks.map((c, i) => (
              <div key={i} className="flex gap-3 rounded-xl border border-zinc-800 bg-[#161618] p-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-300 leading-6">{c}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who it's for */}
        <section className="mt-14 rounded-2xl border border-zinc-800 bg-[#161618] p-6">
          <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Who It’s For</h2>
          <p className="text-[15px] leading-8 text-zinc-300">{service.whoFor}</p>
        </section>

        {/* Long-form content sections */}
        {(service.sections || []).map((sec, i) => (
          <section className="mt-14" key={i} data-testid={`service-section-${i}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>{sec.heading}</h2>
            <div className="space-y-4">
              {sec.body.map((p, j) => (
                <p key={j} className="text-[15px] leading-8 text-zinc-300">{p}</p>
              ))}
            </div>
          </section>
        ))}

        {/* FAQ */}
        {(service.faqs || []).length > 0 && (
          <section className="mt-14" data-testid="service-faq-section">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <HelpCircle className="w-6 h-6 text-orange-400" /> Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {service.faqs.map((f, i) => (
                <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#161618] px-5 py-4 hover:border-orange-500/40 transition-colors" data-testid={`service-faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold text-[15px] md:text-base">
                    <span>{f.q}</span>
                    <ArrowRight className="w-4 h-4 text-orange-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-[15px] leading-7 text-zinc-300">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA band */}
        <section className="mt-14 rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-8 sm:p-10 text-center">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>Ready to know the truth?</h3>
          <p className="mt-2 text-orange-50">Book now and get your CarLens Vehicle Decision Report.</p>
          <Button onClick={() => navigate(service.cta)} className="mt-6 rounded-full bg-white text-orange-600 hover:bg-orange-50 px-8 py-5 h-auto text-base font-semibold" data-testid="service-cta-band-btn">
            {service.ctaLabel} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </section>

        {/* Other services */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-white mb-5" style={{ fontFamily: 'Space Grotesk' }}>Other CarLens Services</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {others.map((s) => {
              const OIcon = ICONS[s.icon] || ShieldCheck;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group flex items-center gap-3 rounded-2xl border border-zinc-800 bg-[#161618] p-5 hover:border-orange-500/50 transition-colors">
                  <OIcon className="w-6 h-6 text-orange-400 shrink-0" />
                  <span className="font-semibold text-white group-hover:text-orange-300 transition-colors">{s.name}</span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Service areas */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><MapPin className="w-5 h-5 text-orange-400" /> Available Across Northeast Florida</h2>
          <div className="flex flex-wrap gap-2.5">
            {LOCATIONS.map((c) => (
              <Link key={c.slug} to={`/locations/${c.slug}`} className="rounded-full border border-zinc-700 bg-[#161618] px-4 py-2 text-sm text-zinc-200 hover:border-orange-500/50 hover:text-orange-300 transition-colors">{c.name}</Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServicePage;
